/**
 * Stale-deploy recovery. A page loaded before a deploy still references the old chunk hashes; after the deploy
 * those files are gone (the server 404s them), so the next lazy route import fails. Reloading once fetches the
 * new index.html. A short session-scoped guard stops a reload loop when a chunk is genuinely broken — the caller
 * then shows an error with a manual reload instead.
 */
const KEY = "mimic.staleDeployReloadAt";
const GUARD_MS = 30_000;

/** Failed dynamic import / chunk preload, across Chromium, Firefox, Safari and Vite's preload helper. */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err ?? "");
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError/i.test(msg);
}

/** Reload the page unless one was already attempted in the last 30 s. Returns whether a reload was started. */
export function reloadForNewDeploy(now: number = Date.now(), reload: () => void = () => window.location.reload()): boolean {
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (now - last < GUARD_MS) return false;
    sessionStorage.setItem(KEY, String(now));
  } catch {
    return false; // storage blocked: no guard, so no automatic reload
  }
  reload();
  return true;
}
